import { useEffect, useRef, useState } from "react";

interface Props {
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  className?: string;
  placeholder?: string;
}

/**
 * Comma-separated list input that doesn't fight the user: the raw text is
 * local state (so a trailing comma survives the keystroke), the parsed list
 * is pushed up on every change, and the display normalizes on blur. Naively
 * deriving the input value from join(parse(...)) strips separators as they
 * are typed — that was pressure-test finding BUG-HIGH #3.
 */
export function CommaListInput({
  value,
  onChange,
  className = "input",
  placeholder,
}: Props): JSX.Element {
  const [raw, setRaw] = useState((value ?? []).join(", "));
  const focused = useRef(false);

  // Adopt external changes (e.g. the editor loads a different record) only
  // while the user isn't mid-keystroke in this field.
  useEffect(() => {
    if (!focused.current) setRaw((value ?? []).join(", "));
  }, [value]);

  function parse(s: string): string[] | null {
    const items = s
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    return items.length > 0 ? items : null;
  }

  return (
    <input
      className={className}
      placeholder={placeholder}
      value={raw}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(parse(e.target.value));
      }}
      onBlur={() => {
        focused.current = false;
        setRaw((parse(raw) ?? []).join(", "));
      }}
    />
  );
}
